// A CSV read as a table in the thread, without a download and without Excel.
//
// "I don't want to force anyone to have to download a shared file to view it." That is
// the whole brief, and it says what this is optimised for: an agent mid-conversation
// reading a customer's file quickly, in place. It is not a spreadsheet. There is no
// editing, no filtering, no formulas and no second page — a column sort over the rows
// already on screen is the only interaction, because scanning and "which of these is
// biggest" are what reading a file in a ticket actually consists of.
//
// EVERY DECISION THIS MAKES LIVES IN ./tabular-preview.js, and that split is the point
// rather than a tidiness habit: this repository has no component-test harness, so a
// sentence or a comparison written inline here could never be checked. What is left in
// this file is elements, classes and the fetch.
//
// THE ONE THING IT MUST NEVER DO is show 1,000 rows of a 50,000-row file as though they
// were the file. The server reports the real totals in `meta`; `truncationNotice`,
// `columnNotice`, `cellNotice` and `sortScopeNotice` turn each one into a sentence, and
// all four are rendered above the table rather than under it.
//
// The bytes are never fetched. This asks §3.2c, which parses the file server-side and
// answers JSON; React then escapes every cell, so a file whose contents are `<script>`
// renders as those characters. See docs/superpowers/specs/2026-10-07-tabular-preview-
// design.md for why a renderable content type is never pointed at these bytes.

import { useEffect, useMemo, useState } from 'react';

import { api } from '../../api/client.js';
import {
  previewApiPath,
  truncationNotice,
  columnNotice,
  cellNotice,
  sortScopeNotice,
  sortRows,
  nextSort,
  ariaSort,
  numericColumnFlags,
  columnLabel,
  delimiterLabel,
  previewErrorMessage,
  formatCount,
} from './tabular-preview.js';

const NO_SORT = { column: null, direction: null };

function SortArrow({ direction }) {
  // Decorative: the heading's `aria-sort` is what carries this to a screen reader, so
  // repeating it as a character would be read out twice.
  if (!direction) return null;
  return (
    <span aria-hidden="true" className="ml-1 text-[0.65rem]">
      {direction === 'desc' ? '▼' : '▲'}
    </span>
  );
}

/**
 * The warnings, and they are rendered ABOVE the table on purpose.
 *
 * A notice under a scrolling table is a notice an agent reaches after they have already
 * decided what the file says.
 */
function Notices({ meta, rowCount, columnCount, sort }) {
  const rows = truncationNotice(meta, rowCount);
  const columns = columnNotice(meta, columnCount);
  const scope = sortScopeNotice(meta, rowCount, sort);
  const cells = cellNotice(meta);
  if (!rows && !columns && !scope && !cells) return null;

  return (
    // Amber rather than the muted grey the delimiter line uses, and on the precedent of
    // the unconfirmed-delivery block in MessageThread.jsx: explicit colours because the
    // shared vocabulary in app.css has no warning set, and amber because this is "part
    // of the file is missing", not an error. Muted grey is what gets skimmed.
    <div
      className="mb-2 space-y-1 rounded-md border px-3 py-2 text-xs font-medium
                 border-amber-400 bg-amber-50 text-amber-900
                 dark:border-amber-600 dark:bg-amber-950 dark:text-amber-200"
    >
      {rows && <p>{rows}</p>}
      {columns && <p>{columns}</p>}
      {scope && <p>{scope}</p>}
      {cells && <p className="font-normal opacity-90">{cells}</p>}
    </div>
  );
}

function Table({ columns, rows, sort, onSort, caption }) {
  const numeric = useMemo(() => numericColumnFlags(rows, columns.length), [rows, columns.length]);

  return (
    // THE SCROLL CONTAINER, and it is what keeps a 60-column customer list from widening
    // the ticket screen: `overflow-auto` here means the table scrolls and the thread
    // does not. `max-h-96` bounds the height for the same reason a long message is not
    // allowed to push the rest of the conversation off screen.
    <div className="max-h-96 max-w-full overflow-auto rounded-md border border-line bg-surface">
      <table className="min-w-full divide-y divide-line text-xs">
        <caption className="sr-only">{caption}</caption>
        {/* STICKY, so the header is still readable 400 rows down. It needs an opaque
            background of its own — a transparent sticky header shows the rows sliding
            under it. */}
        <thead className="sticky top-0 z-10 bg-sunken text-left font-medium text-ink-secondary">
          <tr>
            {columns.map((_, index) => {
              const label = columnLabel(columns, index);
              return (
                <th
                  key={index}
                  scope="col"
                  aria-sort={ariaSort(sort, index)}
                  className="whitespace-nowrap border-b border-line p-0"
                >
                  <button
                    type="button"
                    onClick={() => onSort(index)}
                    className="flex w-full items-center px-2 py-1.5 text-left hover:bg-ink/5
                               focus-visible:outline-2 focus-visible:-outline-offset-2
                               focus-visible:outline-brand-600 dark:focus-visible:outline-brand-400"
                  >
                    {label}
                    <SortArrow direction={sort.column === index ? sort.direction : null} />
                  </button>
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody className="divide-y divide-line">
          {rows.map((row, rowIndex) => (
            <tr key={rowIndex} className="hover:bg-sunken">
              {columns.map((_, index) => (
                <td
                  key={index}
                  // WRAPPED, NOT TRUNCATED. A width cap plus an ellipsis would be a
                  // second truncation this UI never announced, on top of the two the
                  // server already made. Wrapping costs vertical space and hides
                  // nothing. `align-top` keeps a row with one long cell readable.
                  className={`max-w-96 whitespace-pre-wrap break-words px-2 py-1 align-top text-ink ${
                    numeric[index] ? 'text-right font-mono tabular-nums' : ''
                  }`}
                >
                  {row[index] ?? ''}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * One attachment's preview, mounted only while it is open.
 *
 * Mounting is what fetches, and unmounting aborts — so closing a preview mid-request
 * cancels it. Reopening refetches: the design chose parsing on demand over a cache, so
 * there is nothing here to go stale against a file that was re-fetched or purged.
 */
export default function TabularPreview({ publicRef, attachment }) {
  const [state, setState] = useState({ status: 'loading', payload: null, error: null });
  const [sort, setSort] = useState(NO_SORT);

  useEffect(() => {
    const controller = new AbortController();
    setState({ status: 'loading', payload: null, error: null });
    api
      .get(previewApiPath(publicRef, attachment.id), { signal: controller.signal })
      .then((payload) => setState({ status: 'ready', payload, error: null }))
      .catch((error) => {
        if (error?.name === 'AbortError') return;
        setState({ status: 'error', payload: null, error });
      });
    return () => controller.abort();
  }, [publicRef, attachment.id]);

  const columns = state.payload?.data?.columns ?? [];
  const rows = state.payload?.data?.rows ?? [];
  const meta = state.payload?.meta ?? {};
  // Sorted here and not in the fetch: the rows the server sent are kept in their own
  // order so clearing the sort can restore it without asking again.
  const ordered = useMemo(() => sortRows(rows, sort), [rows, sort]);

  if (state.status === 'loading') {
    return <p className="mt-2 text-xs text-ink-muted">Reading {attachment.filename}…</p>;
  }

  if (state.status === 'error') {
    return (
      // The download link is the row directly above this, and it is still live — a 415
      // means the file could not be read HERE, not that it is unavailable. So this says
      // what happened and points back at it rather than offering a second link.
      <p
        className="mt-2 rounded-md border px-3 py-2 text-xs
                   border-amber-400 bg-amber-50 text-amber-900
                   dark:border-amber-600 dark:bg-amber-950 dark:text-amber-200"
      >
        {previewErrorMessage(state.error)} Use the download link above to open it.
      </p>
    );
  }

  if (columns.length === 0) {
    return <p className="mt-2 text-xs text-ink-muted">This file is empty.</p>;
  }

  const delimiter = delimiterLabel(meta.delimiter);

  return (
    <div className="mt-2">
      <Notices meta={meta} rowCount={rows.length} columnCount={columns.length} sort={sort} />

      {rows.length === 0 ? (
        // A header row and nothing under it. Real, and worth saying out loud: an empty
        // table with a header looks like a preview that failed halfway.
        <p className="text-xs text-ink-muted">
          This file has column headings and no data rows.
        </p>
      ) : (
        <Table
          columns={columns}
          rows={ordered}
          sort={sort}
          onSort={(index) => setSort((current) => nextSort(current, index))}
          caption={`Preview of ${attachment.filename}`}
        />
      )}

      <p className="mt-1 text-xs text-ink-muted">
        {/* Naming the delimiter is what tells an agent the preview understood their
            file: a semicolon export read as commas is a valid one-column table, and
            nothing else on screen would distinguish that from a real one-column file. */}
        {delimiter ? `${delimiter}. ` : ''}
        {formatCount(rows.length)} {rows.length === 1 ? 'row' : 'rows'} shown.
        {rows.length > 0 && ' Click a column heading to sort; a third click restores the file’s own order.'}
      </p>
    </div>
  );
}
